import React, {useEffect, useMemo, useState} from 'react';
import {
    getTransactionStatusColorByName,
    TransactionTeam,
    useChangeMilestoneMutation,
    useChecklistMilestonesQuery
} from "@repo/services";
import {Badge, ComboboxItem, lighten, LoadingOverlay, Select} from "@mantine/core";
import './Acquisitions.css';
import {notifications} from "@mantine/notifications";
import {createFormDataObject, opportunityMilestones, useAgMutation} from "@repo/utils";
import {useParams} from "react-router";
import { IconSelector } from '@tabler/icons-react';

/**
 * Displays the current transaction team milestone as a color-coded dropdown Select, letting the user change to another active checklist milestone; renders a static Pending badge instead when the milestone is unset or "Pending".
 *
 * @param milestone - The current AIM checklist milestone code for the transaction team.
 */
const ChecklistMilestoneDropdown = ({ milestone }: { milestone: string }) => {
    const { data: milestonesData = [] } = useChecklistMilestonesQuery(null);
    const { trigger: saveMilestone, isLoading: saveMilestoneIsLoading } = useAgMutation<TransactionTeam>(
        useChangeMilestoneMutation,
        () => {
            notifications.show({
                title: 'Milestone Change Successful',
                message: 'You successfully changed the milestone for this Transaction Team!'
            });
        }
    );
    const [isHovering, setIsHovering] = useState<boolean>(false);

    const { id } = useParams();

    const dropdownData = useMemo(() => {
        return milestonesData?.filter((d) => d?.active)?.map((m) => { return { value: m?.aimChecklistMilestone, label: m?.name } })
    },[milestonesData])

    const currentMilestone = useMemo(() => {
        const current = milestonesData?.find((d) => d.aimChecklistMilestone === milestone);
        if (current) {
            return { value: current?.aimChecklistMilestone, label: current?.name };
        }
        return null;
    },[milestonesData, milestone])

    const [value, setValue] = useState<ComboboxItem | null>(null);

    useEffect(() => setValue(currentMilestone), [currentMilestone]);

    const color = useMemo(() => {
        const milestoneColor = getTransactionStatusColorByName(value?.value || 'default');
        return milestoneColor || 'white';
    },[value, milestonesData, milestone])

    const handleChangeMilestone = (newStatus: ComboboxItem) => {
        const data = createFormDataObject({ aimChecklistMilestone: newStatus.value });
        setValue(newStatus);
        return saveMilestone({ aimTransactionTeam: id, data });
    };

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const subtleBorderStyle = useMemo(() => isHovering && value && color ? `1px solid ${color}` : 'none' ,[value, color, isHovering]);

    if (!milestone || milestone?.toLowerCase() === 'pending') {
        const statusName = opportunityMilestones.find((m) => m.aimChecklistMilestone === 'Pending' && m.type === 'transaction')?.description || milestone;
        return <Badge color="rgba(237, 195, 26, 1)" size="lg" radius="xs" p={15}>{statusName}</Badge>;
    } else {
        return (
            <>
                <LoadingOverlay visible={saveMilestoneIsLoading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
                <Select
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    data={dropdownData}
                    rightSection={<IconSelector size={20} color={value && color ? color : ''} />}
                    //@ts-ignore
                    loading={saveMilestoneIsLoading as any}
                    styles={{
                        input: { borderTop: subtleBorderStyle, borderLeft: subtleBorderStyle, borderRight: subtleBorderStyle, borderBottom: `2px solid ${value && color ? color: 'white'}`, backgroundColor: isHovering && value && color ? lighten(color, 0.9) : 'white', color: value && color ? color : 'black', fontWeight: 'bold' },
                    }}
                    value={value ? value.value : null}
                    onChange={(_value, option) => handleChangeMilestone(option)}
                />
            </>
        );
    }
};

export default ChecklistMilestoneDropdown;
